import React, { useState, useRef, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { FIELD_LIMITS, truncateField } from '../../../utils/validation'

interface Profile {
    email?: string
    full_name?: string | null
    bio?: string | null
    avatar_url?: string | null
}

type NotifyType = 'success' | 'error' | 'warning' | 'info' | 'danger'

interface UpdateResult {
    success: boolean
    error?: string
    email?: string
}

interface ProfileSectionProps {
    profile: Profile | null
    onUpdateProfile: (data: { full_name: string; bio: string }) => Promise<UpdateResult>
    onAvatarUpload: (file: File) => Promise<UpdateResult>
    onPasswordReset: () => Promise<UpdateResult>
    showNotify: (title: string, message: string, type: NotifyType) => void
    isLoading: boolean
}

export default function ProfileSection({
    profile,
    onUpdateProfile,
    onAvatarUpload,
    onPasswordReset,
    showNotify,
    isLoading,
}: Readonly<ProfileSectionProps>) {
    const { t } = useTranslation()
    const [displayName, setDisplayName] = useState(profile?.full_name || '')
    const [bio, setBio] = useState(profile?.bio || '')

    const [prevProfileData, setPrevProfileData] = useState({ full_name: profile?.full_name, bio: profile?.bio })

    if (profile?.full_name !== prevProfileData.full_name || profile?.bio !== prevProfileData.bio) {
        setDisplayName(profile?.full_name || '')
        setBio(profile?.bio || '')
        setPrevProfileData({ full_name: profile?.full_name, bio: profile?.bio })
    }

    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault()
        const result = await onUpdateProfile({ full_name: displayName, bio })

        if (result.success) {
            showNotify(t('settings.profile.notifications.successTitle'), t('settings.profile.notifications.updated'), 'success')
        } else {
            showNotify(t('settings.profile.notifications.errorTitle'), t('settings.profile.notifications.error', { error: result.error }), 'danger')
        }
    }

    const handleAvatarChange = async (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) return

        const file = event.target.files[0]
        const result = await onAvatarUpload(file)

        if (result.success) {
            showNotify(t('settings.profile.notifications.successTitle'), t('settings.profile.notifications.avatarUpdated'), 'success')
        } else {
            showNotify(t('settings.profile.notifications.errorTitle'), t('settings.profile.notifications.avatarError', { error: result.error || 'Error' }), 'danger')
        }
    }

    if (!profile) return null

    return (
        <>
            {/* Profile Section */}
            <section className="settings-section">
                <div className="section-header">
                    <div className="section-icon">👤</div>
                    <div>
                        <h3 className="section-title">{t('settings.profile.title')}</h3>
                        <p className="section-desc">{t('settings.profile.subtitle')}</p>
                    </div>
                </div>

                <div className="profile-content">
                    <div className="avatar-upload-container">
                        <div className="current-avatar">
                            {profile.avatar_url ? (
                                <img src={profile.avatar_url} alt="Avatar" />
                            ) : (
                                <span>{profile.email?.charAt(0).toUpperCase()}</span>
                            )}
                        </div>
                        <div className="upload-overlay">
                            <label htmlFor="avatar-upload" className="upload-label" title="Cambiar foto">
                                📷
                            </label>
                            <input
                                ref={fileInputRef}
                                type="file"
                                id="avatar-upload"
                                accept="image/*"
                                onChange={handleAvatarChange}
                                style={{ display: 'none' }}
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="profile-form">
                        <div className="form-group">
                            <label>{t('settings.profile.emailLabel')}</label>
                            <input
                                type="email"
                                value={profile.email}
                                disabled
                                className="form-input"
                                style={{ background: '#f1f5f9', cursor: 'not-allowed' }}
                            />
                        </div>

                        <div className="form-group">
                            <label>{t('settings.profile.nameLabel')}</label>
                            <input
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(truncateField(e.target.value, FIELD_LIMITS.profiles.full_name))}
                                placeholder={t('settings.profile.namePlaceholder')}
                                className="form-input"
                                maxLength={FIELD_LIMITS.profiles.full_name}
                            />
                            <span
                                className={`char-counter ${displayName.length >= FIELD_LIMITS.profiles.full_name * 0.9 ? 'warning' : ''}`}
                                style={{ fontSize: '0.8rem', textAlign: 'right', display: 'block', marginTop: '4px' }}
                            >
                                {displayName.length}/{FIELD_LIMITS.profiles.full_name}
                            </span>
                        </div>

                        <div className="form-group">
                            <label>{t('settings.profile.bioLabel')}</label>
                            <textarea
                                value={bio}
                                onChange={(e) => setBio(truncateField(e.target.value, FIELD_LIMITS.profiles.bio))}
                                placeholder={t('settings.profile.bioPlaceholder')}
                                className="form-textarea"
                                maxLength={FIELD_LIMITS.profiles.bio}
                                rows={4}
                            />
                            <span
                                className={`char-counter ${bio.length >= FIELD_LIMITS.profiles.bio * 0.9 ? 'warning' : ''}`}
                                style={{ fontSize: '0.8rem', textAlign: 'right', display: 'block', marginTop: '4px' }}
                            >
                                {bio.length}/{FIELD_LIMITS.profiles.bio}
                            </span>
                        </div>

                        <button type="submit" className="btn btn-primary" disabled={isLoading}>
                            {isLoading ? t('settings.profile.saving') : t('settings.profile.save')}
                        </button>
                    </form>
                </div>
            </section>

            {/* Password Management */}
            <section className="settings-section">
                <div className="section-header">
                    <div className="section-icon">🔒</div>
                    <div>
                        <h3 className="section-title">{t('settings.profile.security.title')}</h3>
                        <p className="section-desc">{t('settings.profile.security.subtitle')}</p>
                    </div>
                </div>

                <div className="security-content">
                    <p style={{ marginBottom: '1rem', color: '#475569' }}>
                        {t('settings.profile.security.resetText')}
                    </p>
                    <button
                        onClick={async () => {
                            const result = await onPasswordReset()
                            if (result.success) {
                                showNotify(t('settings.profile.notifications.resetSentTitle'), t('settings.profile.notifications.resetSent', { email: result.email }), 'info')
                            } else {
                                showNotify(t('settings.profile.notifications.errorTitle'), t('settings.profile.notifications.resetError', { error: result.error }), 'danger')
                            }
                        }}
                        className="btn btn-secondary"
                        disabled={isLoading}
                    >
                        {t('settings.profile.security.sendReset')}
                    </button>
                </div>
            </section>
        </>
    )
}
